import { removeCharUntilMax, isEmpty } from "@utils";
import {
	PAGES,
	UNSCRAMBLER_MAX_WILDCARDS,
	ADVANCED_FIELD_TYPES,
} from "@consts";

export default {
	name: "WordsByLengthFinder",
	data() {
		return {
			advancedFields: {
				[ADVANCED_FIELD_TYPES.STARTS]: "",
				[ADVANCED_FIELD_TYPES.WITH]: "",
				[ADVANCED_FIELD_TYPES.ENDS]: "",
				[ADVANCED_FIELD_TYPES.LENGTH]: null,
			},
		};
	},
	computed: {
		advancedFieldsData() {
			const advancedFieldKeys = [
				ADVANCED_FIELD_TYPES.STARTS,
				ADVANCED_FIELD_TYPES.WITH,
				ADVANCED_FIELD_TYPES.ENDS,
				ADVANCED_FIELD_TYPES.LENGTH,
			];

			return advancedFieldKeys.map((key) => {
				return {
					fieldType: key,
					value: String(this.advancedFields[key]),
				};
			});
		},
		isSingleFieldWordLengthLandingPage() {
			return [
				PAGES.WORDS_BY_LENGTH_LANDING_5_STARTS,
				PAGES.WORDS_BY_LENGTH_LANDING_5_ENDS,
				PAGES.WORDS_BY_LENGTH_LANDING_5_HAVING,
			].includes(this.$route.name);
		},
		submitButtonText() {
			switch (this.$route.name) {
				case PAGES.WORDS_BY_LENGTH_LANDING_4:
					return "Generate 4 Letter Word List";
				case PAGES.WORDS_BY_LENGTH_LANDING_5:
					return "Generate 5 Letter Word List";
				case PAGES.WORDS_BY_LENGTH_LANDING_5_STARTS:
				case PAGES.WORDS_BY_LENGTH_LANDING_5_ENDS:
				case PAGES.WORDS_BY_LENGTH_LANDING_5_HAVING:
					return "Generate Word List";
				case PAGES.WORDS_BY_LENGTH_LANDING_6:
					return "Generate 6 Letter Word List";
				case PAGES.WORDS_BY_LENGTH_LANDING_7:
					return "Generate 7 Letter Word List";
				case PAGES.WORDS_BY_LENGTH_LANDING_8:
					return "Generate 8 Letter Word List";
				default:
					return "Generate 4 Letter Word List";
			}
		},
		totalWildcardCount() {
			const wildcardCount = [
				this.advancedFields[ADVANCED_FIELD_TYPES.STARTS],
				this.advancedFields[ADVANCED_FIELD_TYPES.ENDS],
			].map((value) => {
				return (value.match(/\?/g) || []).length;
			});

			wildcardCount.push(
				(
					this.advancedFields[ADVANCED_FIELD_TYPES.WITH].match(
						/_/g
					) || []
				).length
			);

			return wildcardCount.reduce((total, current) => total + current, 0);
		},
	},
	methods: {
		onAdvancedSearchControlInput(type, value) {
			this.onFieldInput(type, value);
		},
		onFieldInput(fieldType, value) {
			const isContains = fieldType === ADVANCED_FIELD_TYPES.WITH;
			let fieldValidationRegex = isContains
				? /[^a-zA-Z_]/g
				: /[^a-zA-Z?]/g;

			if (
				[
					ADVANCED_FIELD_TYPES.STARTS,
					ADVANCED_FIELD_TYPES.ENDS,
				].includes(fieldType)
			) {
				fieldValidationRegex = /[^a-zA-Z]/g;
			}

			const wildcardCharacter = isContains ? "_" : "?";
			const wildcardRegex = isContains ? /_/g : /\?/g;

			const oldValue = this.advancedFields[fieldType];

			let newValue = (value || "")
				.replace(/[\s?]/g, wildcardCharacter)
				.replace(fieldValidationRegex, "")
				.toUpperCase();

			if (isContains) {
				const oldWildcardCount = (oldValue.match(wildcardRegex) || [])
					.length;
				const newWildcardCount = (newValue.match(wildcardRegex) || [])
					.length;

				const oldTotalWildcardCount = this.totalWildcardCount;
				const newTotalWildcardCount =
					oldTotalWildcardCount - oldWildcardCount + newWildcardCount;

				const wildcardsToRemove =
					newTotalWildcardCount - UNSCRAMBLER_MAX_WILDCARDS;

				if (wildcardsToRemove > 0) {
					newValue = removeCharUntilMax(
						newValue,
						wildcardCharacter,
						newWildcardCount - wildcardsToRemove
					);
				}
			}

			const otherFieldsTotal = Object.keys(ADVANCED_FIELD_TYPES)
				.map((key) => ADVANCED_FIELD_TYPES[key])
				.filter(
					(type) =>
						type !== fieldType &&
						type !== ADVANCED_FIELD_TYPES.LENGTH
				)
				.reduce((sum, type) => {
					return sum + (this.advancedFields[type] || "").length;
				}, 0);

			const maxForThisField =
				this.advancedFields[ADVANCED_FIELD_TYPES.LENGTH] -
				otherFieldsTotal;

			if (maxForThisField <= 0) {
				newValue = "";
			} else if (newValue.length > maxForThisField) {
				newValue = newValue.slice(0, maxForThisField);
			}

			this.advancedFields[fieldType] = newValue;
			return newValue;
		},
		getWordListResultsPagePath() {
			const startsWithNoWildcards = removeCharUntilMax(
				this.advancedFields[ADVANCED_FIELD_TYPES.STARTS],
				"?",
				0
			);
			const endsWithNoWildcards = removeCharUntilMax(
				this.advancedFields[ADVANCED_FIELD_TYPES.ENDS],
				"?",
				0
			);
			const containsNoWildcards = removeCharUntilMax(
				this.advancedFields[ADVANCED_FIELD_TYPES.WITH],
				"_",
				0
			);

			const contains = this.advancedFields[ADVANCED_FIELD_TYPES.WITH];

			const hasStartsWith = !isEmpty(startsWithNoWildcards);
			const hasContains = !isEmpty(containsNoWildcards);
			const hasEndsWith = !isEmpty(endsWithNoWildcards);
			const hasLength = !isEmpty(
				this.advancedFields[ADVANCED_FIELD_TYPES.LENGTH]
			);

			const base = hasLength ? "letter-words" : "words-with-the-letter";

			const segments = [];

			if (hasLength) {
				segments.push(this.advancedFields[ADVANCED_FIELD_TYPES.LENGTH]);
			}

			if (hasStartsWith) {
				segments.push(`starts-${startsWithNoWildcards}`);
			}

			if (hasContains) {
				const onlyHasContains =
					hasContains && !hasStartsWith && !hasEndsWith && !hasLength;

				segments.push(onlyHasContains ? contains : `with-${contains}`);
			}

			if (hasEndsWith) {
				segments.push(`ends-${endsWithNoWildcards}`);
			}

			return `/${base}/${segments.join("-")}/`;
		},
		onFormSubmit() {
			this.$router.push(this.getWordListResultsPagePath().toLowerCase());
		},
	},
	created() {
		switch (this.$route.name) {
			case PAGES.WORDS_BY_LENGTH_LANDING_4:
				this.advancedFields[ADVANCED_FIELD_TYPES.LENGTH] = "4";
				break;
			case PAGES.WORDS_BY_LENGTH_LANDING_5:
			case PAGES.WORDS_BY_LENGTH_LANDING_5_STARTS:
			case PAGES.WORDS_BY_LENGTH_LANDING_5_ENDS:
			case PAGES.WORDS_BY_LENGTH_LANDING_5_HAVING:
				this.advancedFields[ADVANCED_FIELD_TYPES.LENGTH] = "5";
				break;
			case PAGES.WORDS_BY_LENGTH_LANDING_6:
				this.advancedFields[ADVANCED_FIELD_TYPES.LENGTH] = "6";
				break;
			case PAGES.WORDS_BY_LENGTH_LANDING_7:
				this.advancedFields[ADVANCED_FIELD_TYPES.LENGTH] = "7";
				break;
			case PAGES.WORDS_BY_LENGTH_LANDING_8:
				this.advancedFields[ADVANCED_FIELD_TYPES.LENGTH] = "8";
				break;
			default:
				this.advancedFields[ADVANCED_FIELD_TYPES.LENGTH] = "";
		}
	},
};
